import React, { useEffect, useState } from 'react'
import { useAuth } from '../../../context/useAuth'
import { useNls } from '../../../nls/nlsProvider'
import { useMyAccessScope } from '../../../hooks/useMyAccessScope'
import type {
  OpsBooking,
  OpsCustomer,
  OpsBookable,
  OpsBookingSession,
} from '../../../types/ops'
import {
  DEFAULT_OPS_BOOKING_PLAN_POLICY,
  type OpsBookingPlanPolicy,
} from '../../../types/ops'
import { getEnabledBookingSectors } from '../../../utils/workspaceModules'
import { toLocalIsoDate } from '../../../utils/localDateTimeInput'
import type { Workspace } from '../../../types/workspace'
import { AppDialog, Button, StatusPill } from '../../Common'
import {
  cancelBooking,
  leaveWaitlist,
  moveBooking,
  subscribeBookables,
  subscribeBookingPlan,
  subscribeBookingSessions,
  subscribeBookingsByCustomer,
  subscribeMembershipsByCustomer,
} from '../../../services/opsBookingsService'
import { expandOccurrences } from '../../../types/bookingsCore'
import { formatMemberBookingError } from './memberBookingsErrorMapper'

export interface MemberBookingsListTabProps {
  workspace: Workspace
  customer: OpsCustomer
}

function createOpId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `op_${Date.now()}`
}

export function MemberBookingsListTab({
  workspace,
  customer,
}: MemberBookingsListTabProps): React.ReactElement {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const { restricted: isRestricted } = useMyAccessScope(workspace.id)

  const [filter, setFilter] = useState<'UPCOMING' | 'PAST'>('UPCOMING')
  const [bookings, setBookings] = useState<OpsBooking[]>([])
  const [bookables, setBookables] = useState<OpsBookable[]>([])
  const [planPolicy, setPlanPolicy] = useState<OpsBookingPlanPolicy>(
    DEFAULT_OPS_BOOKING_PLAN_POLICY,
  )
  const [moveSessions, setMoveSessions] = useState<Record<string, OpsBookingSession>>({})
  const [busy, setBusy] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Modals state
  const [cancelTarget, setCancelTarget] = useState<OpsBooking | null>(null)
  const [leaveTarget, setLeaveTarget] = useState<OpsBooking | null>(null)
  const [moveTarget, setMoveTarget] = useState<OpsBooking | null>(null)
  const [selectedMoveDate, setSelectedMoveDate] = useState<string>('')
  const [nowMs] = useState(() => Date.now())

  useEffect(() => {
    let unsubs: Array<() => void> = []
    if (workspace.id && customer.id) {
      const currentUid = currentUser?.uid

      const unsubB = subscribeBookingsByCustomer(
        workspace.id,
        customer.id,
        (items) => setBookings(items),
        (err) => setErrorMsg(formatMemberBookingError(err, t)),
        { callerUid: currentUid },
      )
      unsubs.push(unsubB)

      const activeSectors = getEnabledBookingSectors(workspace)
      activeSectors.forEach((sec) => {
        const unsubBk = subscribeBookables(
          workspace.id,
          sec,
          (items) => {
            setBookables((prev) => {
              const others = prev.filter((b) => b.sector !== sec)
              return [...others, ...items]
            })
          },
          (err) => setErrorMsg(formatMemberBookingError(err, t)),
          { isRestricted, callerUid: currentUid },
        )
        unsubs.push(unsubBk)
      })

      const unsubMem = subscribeMembershipsByCustomer(
        workspace.id,
        customer.id,
        (mems) => {
          const activeMem = mems.find((m) => m.state === 'ACTIVE')
          if (activeMem?.planSnapshot?.policy) {
            setPlanPolicy(activeMem.planSnapshot.policy)
          }
          if (activeMem?.planId) {
            const unsubPlan = subscribeBookingPlan(
              activeMem.planId,
              (plan) => {
                if (plan?.policy) {
                  setPlanPolicy(plan.policy)
                }
              },
              () => {},
            )
            if (typeof unsubPlan === 'function') unsubs.push(unsubPlan)
          }
        },
        () => {},
      )
      unsubs.push(unsubMem)
    }

    return () => {
      unsubs.forEach((u) => {
        if (typeof u === 'function') u()
      })
      unsubs = []
    }
  }, [workspace, customer.id, currentUser?.uid, isRestricted, t])

  useEffect(() => {
    if (!moveTarget || !workspace.id) {
      return
    }
    const currentUid = currentUser?.uid
    const targetBk = bookables.find((bk) => bk.id === moveTarget.bookableId)
    if (!targetBk) return

    const effectiveBookAhead = Math.min(
      targetBk.bookAheadDays ?? DEFAULT_OPS_BOOKING_PLAN_POLICY.bookAheadDays,
      planPolicy.bookAheadDays ?? DEFAULT_OPS_BOOKING_PLAN_POLICY.bookAheadDays,
    )
    const moveTodayIso = toLocalIsoDate(nowMs)
    const maxFutureIso = toLocalIsoDate(nowMs + effectiveBookAhead * 86400000)

    const unsub = subscribeBookingSessions(
      workspace.id,
      targetBk.sector,
      moveTodayIso,
      (sessList) => {
        setMoveSessions((prev) => {
          const next = { ...prev }
          sessList.forEach((s) => {
            next[`${s.bookableId}_${s.date}`] = s
          })
          return next
        })
      },
      (err) => setErrorMsg(formatMemberBookingError(err, t)),
      { isRestricted, callerUid: currentUid, bookableId: targetBk.id, toDate: maxFutureIso },
    )

    return () => {
      if (typeof unsub === 'function') unsub()
    }
  }, [
    moveTarget,
    workspace.id,
    bookables,
    isRestricted,
    currentUser?.uid,
    planPolicy.bookAheadDays,
    nowMs,
    t,
  ])

  const handleConfirmCancel = async () => {
    if (!cancelTarget || busy) return
    setBusy(true)
    setErrorMsg(null)
    try {
      const opId = createOpId()
      await cancelBooking({
        workspaceId: workspace.id,
        bookingId: cancelTarget.id,
        opId,
      })
      setCancelTarget(null)
    } catch (err: unknown) {
      setErrorMsg(
        formatMemberBookingError(err, t, {
          action: 'cancel',
          blockedUntil: customer.blockedUntil,
        }),
      )
    } finally {
      setBusy(false)
    }
  }

  const handleConfirmLeaveWaitlist = async () => {
    if (!leaveTarget || busy) return
    setBusy(true)
    setErrorMsg(null)
    try {
      const opId = createOpId()
      await leaveWaitlist({
        workspaceId: workspace.id,
        bookingId: leaveTarget.id,
        opId,
      })
      setLeaveTarget(null)
    } catch (err: unknown) {
      setErrorMsg(
        formatMemberBookingError(err, t, {
          action: 'leaveWaitlist',
          blockedUntil: customer.blockedUntil,
        }),
      )
    } finally {
      setBusy(false)
    }
  }

  const handleConfirmMove = async () => {
    if (!moveTarget || !selectedMoveDate || busy) return
    setBusy(true)
    setErrorMsg(null)
    try {
      const opId = createOpId()
      await moveBooking({
        workspaceId: workspace.id,
        bookingId: moveTarget.id,
        targetBookableId: moveTarget.bookableId,
        targetDate: selectedMoveDate,
        opId,
      })
      setMoveTarget(null)
      setSelectedMoveDate('')
      setMoveSessions({})
    } catch (err: unknown) {
      setErrorMsg(
        formatMemberBookingError(err, t, {
          action: 'move',
          blockedUntil: customer.blockedUntil,
        }),
      )
    } finally {
      setBusy(false)
    }
  }

  const upcomingBookings = bookings.filter(
    (b) => b.state === 'BOOKED' || b.state === 'WAITLISTED',
  )
  const pastBookings = bookings.filter(
    (b) =>
      b.state === 'ATTENDED' ||
      b.state === 'NO_SHOW' ||
      b.state === 'CANCELLED' ||
      b.state === 'LATE_CANCELLED',
  )

  const displayedList = filter === 'UPCOMING' ? upcomingBookings : pastBookings

  // Move options: find alternate dates for the targeted bookable within book-ahead window
  const targetBookable = moveTarget
    ? bookables.find((bk) => bk.id === moveTarget.bookableId)
    : null
  const effectiveBookAhead = Math.min(
    targetBookable?.bookAheadDays ?? DEFAULT_OPS_BOOKING_PLAN_POLICY.bookAheadDays,
    planPolicy.bookAheadDays ?? DEFAULT_OPS_BOOKING_PLAN_POLICY.bookAheadDays,
  )
  const todayIso = toLocalIsoDate(nowMs)
  const futureIso = toLocalIsoDate(nowMs + effectiveBookAhead * 86400000)

  const now = new Date(nowMs)
  const currentHour = now.getHours()
  const currentMinute = now.getMinutes()
  const [startH, startM] = (targetBookable?.pattern?.startTime || '00:00')
    .split(':')
    .map(Number)

  const alternateDates = targetBookable?.pattern
    ? expandOccurrences(targetBookable.pattern, [], todayIso, futureIso).filter((d) => {
        if (d === moveTarget?.date) return false
        if (d < todayIso) return false
        if (d === todayIso) {
          if (currentHour > startH || (currentHour === startH && currentMinute >= startM)) {
            return false
          }
        }
        return true
      })
    : []

  const isLateCancel = Boolean(
    cancelTarget &&
      cancelTarget.startAt &&
      cancelTarget.startAt - nowMs <= planPolicy.cancelWindowHours * 3600 * 1000 &&
      planPolicy.lateCancelUsesCredit,
  )

  return (
    <div className="memberBookingsListTab">
      <div className="memberBookingsListTab__filters">
        <Button
          variant={filter === 'UPCOMING' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setFilter('UPCOMING')}
        >
          {t('NLSMemberBookingsFilterUpcoming')} ({upcomingBookings.length})
        </Button>
        <Button
          variant={filter === 'PAST' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setFilter('PAST')}
        >
          {t('NLSMemberBookingsFilterPast')} ({pastBookings.length})
        </Button>
      </div>

      {errorMsg && (
        <div className="memberLinkBookingsCard__error" role="alert">
          {errorMsg}
        </div>
      )}

      {displayedList.length === 0 ? (
        <p className="memberBookingsListTab__empty">
          {filter === 'UPCOMING'
            ? t('NLSMemberBookingsEmptyUpcoming')
            : t('NLSMemberBookingsEmptyPast')}
        </p>
      ) : (
        <ul className="memberBookingsListTab__list">
          {displayedList.map((booking) => {
            const isWaitlisted = booking.state === 'WAITLISTED'
            const isBooked = booking.state === 'BOOKED'
            const isAttended = booking.state === 'ATTENDED'
            const isNoShow = booking.state === 'NO_SHOW'
            const isCancelled = booking.state === 'CANCELLED'
            const isLateCancelled = booking.state === 'LATE_CANCELLED'

            let stateTone: 'success' | 'warning' | 'danger' | 'neutral' = 'neutral'
            let stateLabel: string = booking.state
            if (isBooked) {
              stateTone = 'success'
              stateLabel = t('NLSMemberBookingsStateBooked')
            } else if (isWaitlisted) {
              stateTone = 'warning'
              stateLabel = t('NLSMemberBookingsStateWaitlisted', {
                position: booking.waitlistPosition || 1,
              })
            } else if (isAttended) {
              stateTone = 'success'
              stateLabel = t('NLSMemberBookingsStateAttended')
            } else if (isNoShow) {
              stateTone = 'danger'
              stateLabel = t('NLSMemberBookingsStateNoShow')
            } else if (isCancelled) {
              stateTone = 'neutral'
              stateLabel = t('NLSMemberBookingsStateCancelled')
            } else if (isLateCancelled) {
              stateTone = 'danger'
              stateLabel = t('NLSMemberBookingsStateLateCancelled')
            }

            const bookable = bookables.find((b) => b.id === booking.bookableId)
            const bookableTitle = bookable?.name || booking.bookableId
            // Under firestore.rules opsBkR (:4324-4335) ops_places requires opsAccR(w, d.readAccess)
            // which requires opsR(w) (workspace owner, active member, or helper). End-user members/customers
            // are not permitted to read ops_places, so no place name is displayed in the member bookings list.
            const placeName = ''

            return (
              <li key={booking.id} className="memberBookingsListTab__item">
                <div className="memberBookingsListTab__itemHeader">
                  <h3 className="memberBookingsListTab__itemTitle">{bookableTitle}</h3>
                  <StatusPill tone={stateTone}>{stateLabel}</StatusPill>
                </div>
                <div className="memberBookingsListTab__itemMeta">
                  <span>{booking.date}</span>
                  {placeName && <span>{placeName}</span>}
                </div>
                {filter === 'UPCOMING' && (
                  <div className="memberBookingsListTab__itemActions">
                    {isBooked && (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={busy}
                          onClick={() => setMoveTarget(booking)}
                        >
                          {t('NLSMemberBookingsMoveAction')}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={busy}
                          onClick={() => setCancelTarget(booking)}
                        >
                          {t('NLSMemberBookingsCancelAction')}
                        </Button>
                      </>
                    )}
                    {isWaitlisted && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={busy}
                        onClick={() => setLeaveTarget(booking)}
                      >
                        {t('NLSMemberBookingsLeaveWaitlistAction')}
                      </Button>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {/* Cancel Confirmation Modal */}
      {cancelTarget && (
        <AppDialog
          open={Boolean(cancelTarget)}
          title={t('NLSMemberBookingsConfirmCancelTitle')}
          onDismiss={() => setCancelTarget(null)}
          actions={[
            {
              label: t('NLSCommonActionClose'),
              variant: 'secondary',
              onClick: () => setCancelTarget(null),
            },
            {
              label: t('NLSMemberBookingsCancelAction'),
              variant: 'danger',
              disabled: busy,
              onClick: handleConfirmCancel,
            },
          ]}
        >
          <div className="memberBookingsListTab__itemMeta">
            <p>{t('NLSMemberBookingsConfirmCancelDesc', { time: cancelTarget.date })}</p>
            {isLateCancel && (
              <p className="memberBookingsListTab__lateNotice">
                {t('NLSMemberBookingsLateCancelWarning', {
                  hours: planPolicy.cancelWindowHours,
                })}
              </p>
            )}
          </div>
        </AppDialog>
      )}

      {/* Leave Waitlist Confirmation Modal */}
      {leaveTarget && (
        <AppDialog
          open={Boolean(leaveTarget)}
          title={t('NLSMemberBookingsConfirmLeaveWaitlist')}
          onDismiss={() => setLeaveTarget(null)}
          actions={[
            {
              label: t('NLSCommonActionCancel'),
              variant: 'secondary',
              onClick: () => setLeaveTarget(null),
            },
            {
              label: t('NLSMemberBookingsLeaveWaitlistAction'),
              variant: 'primary',
              disabled: busy,
              onClick: handleConfirmLeaveWaitlist,
            },
          ]}
        >
          <p>{t('NLSMemberBookingsConfirmLeaveWaitlist')}</p>
        </AppDialog>
      )}

      {/* Move Booking Modal */}
      {moveTarget && (
        <AppDialog
          open={Boolean(moveTarget)}
          title={t('NLSMemberBookingsMoveModalTitle')}
          onDismiss={() => {
            setMoveTarget(null)
            setSelectedMoveDate('')
            setMoveSessions({})
          }}
          actions={[
            {
              label: t('NLSCommonActionCancel'),
              variant: 'secondary',
              onClick: () => {
                setMoveTarget(null)
                setSelectedMoveDate('')
                setMoveSessions({})
              },
            },
            {
              label: t('NLSMemberBookingsMoveAction'),
              variant: 'primary',
              disabled: busy || !selectedMoveDate,
              onClick: handleConfirmMove,
            },
          ]}
        >
          <div className="memberBookingsListTab__itemMeta">
            <p>{t('NLSMemberBookingsSelectSessionPrompt')}</p>
            {alternateDates.length === 0 ? (
              <p className="memberBookingsListTab__empty">
                {t('NLSMemberBookingsNoAlternateSessions')}
              </p>
            ) : (
              <div className="memberBookTab__occurrences">
                {alternateDates.map((d) => {
                  const sessKey = `${targetBookable?.id}_${d}`
                  const sess = moveSessions[sessKey]
                  const cap = sess?.capacity ?? targetBookable?.capacity ?? 1
                  const bk = sess?.booked ?? 0
                  const seats = Math.max(0, cap - bk)
                  const isSessionFull = seats <= 0
                  const startTime =
                    sess?.startTime || targetBookable?.pattern?.startTime || ''

                  return (
                    <div key={d} className="memberBookTab__occurrenceRow">
                      <div className="memberBookingsListTab__itemMeta">
                        <strong>
                          {d}
                          {startTime ? ` · ${startTime}` : ''}
                        </strong>
                        <span>
                          {isSessionFull
                            ? t('NLSMemberBookingsSessionFull')
                            : t('NLSMemberBookingsSeatsLeft', { count: seats })}
                        </span>
                      </div>
                      <Button
                        variant={selectedMoveDate === d ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setSelectedMoveDate(d)}
                      >
                        {d}
                      </Button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </AppDialog>
      )}
    </div>
  )
}
